import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { scorecardTemplateApi, ScorecardTemplate, ScorecardCriterion, InterviewQuestion } from '../services/recruitingApi';

export const scorecardTemplateKeys = {
  all: ['scorecardTemplates'] as const,
  list: () => [...scorecardTemplateKeys.all, 'list'] as const,
  detail: (id: string) => [...scorecardTemplateKeys.all, 'detail', id] as const,
};

export function useScorecardTemplates() {
  return useQuery({
    queryKey: scorecardTemplateKeys.list(),
    queryFn: () => scorecardTemplateApi.list(),
  });
}

export function useScorecardTemplate(id: string) {
  return useQuery({
    queryKey: scorecardTemplateKeys.detail(id),
    queryFn: () => scorecardTemplateApi.get(id),
    enabled: !!id,
  });
}

export function useCreateScorecardTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      name: string;
      description?: string;
      stage_type?: string;
      criteria?: ScorecardCriterion[];
      interview_questions?: InterviewQuestion[];
      is_default?: boolean;
    }) => scorecardTemplateApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scorecardTemplateKeys.all });
    },
  });
}

export function useUpdateScorecardTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ScorecardTemplate> }) =>
      scorecardTemplateApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: scorecardTemplateKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: scorecardTemplateKeys.list() });
    },
  });
}

export function useDeleteScorecardTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => scorecardTemplateApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scorecardTemplateKeys.all });
    },
  });
}
